import { ENDPOINT } from '~/constants';

import { type UUID } from '~/types/common';

import { HTTPError, vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

import { type CompanyAccountResponse } from './types';
import { zodCompanyAccountResponseSchema } from './validations';

/**
 * Fetches a single company account with a given ID.
 *
 * @param params - The parameters for fetching the company account.
 * @returns The company account data.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Company-Account/operation/get_company_account_company_account__company_account_uuid__get
 */
export const fetchCompanyAccount = async (companyAccountId: UUID) => {
  try {
    return await vestigasApi
      .get(ENDPOINT.COMPANY.ACCOUNT.GET(companyAccountId), {
        validate: { response: zodCompanyAccountResponseSchema },
      })
      .json<CompanyAccountResponse>();
  } catch (error) {
    log.error({
      message: 'Error fetching company account',
      error,
    });

    let errorMessage = '';

    if (error instanceof HTTPError) {
      switch (error.response.status) {
        case 404: {
          errorMessage = i18n.t(
            ($) => $.companyAccount.fetchCompanyAccount.error404,
            { ns: 'data' },
          );
          break;
        }

        default: {
          errorMessage = i18n.t(
            ($) => $.companyAccount.fetchCompanyAccount.error.generic,
            { ns: 'data' },
          );
        }
      }
    }

    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};
